import * as React from "react";
import { useMemo, useState } from "react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { TableColumnsType } from "antd";
import { AntDTable } from "@/common/components/ui";
import { ContactIcon, Eye, Search, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/common/components/ui/dialog";
import { Textarea } from "@/common/components/ui/textarea";
import { notify } from "@/common/components/common/Notification";
import { Badge } from "@/projects/FinEaseHomeAndAdmin/components/ui/badge";
import * as XLSX from "xlsx";

interface ContactQuery {
  id: string;
  contactPerson: string;
  subject: string;
  phone: string;
  email: string;
  date: string;
  status: "new" | "responded" | "pending";
  message: string;
  previousResponse?: string;
}

const MOCK_CONTACTS: ContactQuery[] = [
  {
    id: "1",
    contactPerson: "testt",
    subject: "No",
    phone: "0111111111",
    email: "admin@universal-software.com",
    date: "05-03-2026",
    status: "responded",
    message: "testt",
    previousResponse: "teysrf",
  },
  {
    id: "2",
    contactPerson: "Vaja sunil",
    subject: "I Need to help for Training",
    phone: "07096247845",
    email: "vajasunil04@gmail.com",
    date: "25-02-2026",
    status: "responded",
    message: "Need trainings schedule",
    previousResponse: "Scheduled next week",
  },
  {
    id: "3",
    contactPerson: "QA TESTING",
    subject: "FOR TESTING",
    phone: "09104293249",
    email: "prateekm.universalsoftware@gmail.com",
    date: "20-02-2026",
    status: "responded",
    message: "QA testing details",
    previousResponse: "Responded",
  },
  {
    id: "4",
    contactPerson: "Hiren Jain",
    subject: "GHJ",
    phone: "7766554433",
    email: "hiren@universal-software.com",
    date: "19-02-2026",
    status: "responded",
    message: "GHJ",
    previousResponse: "Followed up",
  },
  {
    id: "5",
    contactPerson: "SHRI SHYAMLAL JAIN",
    subject: "ttttt",
    phone: "09312211886",
    email: "techsupport@universal-software.com",
    date: "19-02-2026",
    status: "new",
    message: "Need assistance",
  },
];

const statusAppearance: Record<ContactQuery["status"], string> = {
  new: "bg-amber-50 text-amber-700",
  responded: "bg-emerald-50 text-emerald-700",
  pending: "bg-slate-100 text-slate-700",
};

const ContactUsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedQuery, setSelectedQuery] = useState<ContactQuery | null>(null);
  const [reply, setReply] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ContactQuery[]>([]);

  const filtered = useMemo(
    () =>
      MOCK_CONTACTS.filter(query =>
        query.contactPerson.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm]
  );

  const columns: TableColumnsType<ContactQuery> = useMemo(
    () => [
      {
        title: "Sr. No",
        dataIndex: "id",
        key: "id",
        width: 80,
        render: (_, __, idx) => idx + 1,
      },
      {
        title: "Contact Person Name",
        dataIndex: "contactPerson",
        key: "contactPerson",
      },
      {
        title: "Subject",
        dataIndex: "subject",
        key: "subject",
      },
      {
        title: "Phone Number",
        dataIndex: "phone",
        key: "phone",
      },
      {
        title: "Email ID",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: ContactQuery["status"]) => (
          <Badge className={statusAppearance[status]}>{status}</Badge>
        ),
      },
      {
        title: "Action",
        key: "action",
        width: 140,
        render: (_, query) => (
          <Button
            variant="ghost"
            className="h-8 px-3"
            onClick={() => setSelectedQuery(query)}
          >
            <Eye className="h-4 w-4" />
            <span className="sr-only">View</span>
          </Button>
        ),
      },
    ],
    []
  );

  const handleSendReply = () => {
    if (!selectedQuery) {
      return;
    }
    notify.success("Response sent", "Your reply has been recorded.");
    setReply("");
    setSelectedQuery(null);
  };

  const onSelectChange = (keys: React.Key[], rows: ContactQuery[]) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select items to export.");
      return;
    }

    const rows = selectedRows.map(item => ({
      ID: item.id,
      "Contact Person": item.contactPerson,
      Subject: item.subject,
      Phone: item.phone,
      Email: item.email,
      Date: item.date,
      Status: item.status,
      Message: item.message,
      "Previous Response": item.previousResponse ?? "-",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "ContactQueries");

    XLSX.writeFile(
      wb,
      `contact_queries_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    notify.success("Exported", `${rows.length} record(s) exported.`);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <ContactIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Contact Us
              </h1>
              <p className="text-white/70">
                Manage enquiries coming via Contact Us.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              className="border-0 bg-white text-primary shadow-lg hover:bg-white/90"
              onClick={handleExport}
              disabled={selectedRows.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-slate-600">
                  {selectedRows.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-2xl border bg-card shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by contact person name..."
                  className="w-96 pl-10 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div>
        <AntDTable<ContactQuery>
          showSettings
          data={filtered}
          columns={columns}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectChange,
          }}
          pagination={{ pageSize: 10 }}
          emptyText="No contact queries"
        />
      </div>

      <Dialog
        open={Boolean(selectedQuery)}
        onOpenChange={open => {
          if (!open) {
            setSelectedQuery(null);
            setReply("");
          }
        }}
      >
        {selectedQuery && (
          <DialogContent className="w-full max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-sans text-2xl font-semibold">
                Contact Query Details
              </DialogTitle>
              <DialogDescription>
                <p className="font-sans text-sm text-muted-foreground">
                  View the enquiry and add a reply.
                </p>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 font-sans">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Contact Person
                  </p>
                  <p className="text-sm">{selectedQuery.contactPerson}</p>
                </div>
                <div>
                  <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Subject
                  </p>
                  <p className="text-sm">{selectedQuery.subject}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Phone
                  </p>
                  <p className="text-sm">{selectedQuery.phone}</p>
                </div>
                <div>
                  <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Email
                  </p>
                  <p className="text-sm">{selectedQuery.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Date
                  </p>
                  <p className="text-sm">{selectedQuery.date}</p>
                </div>
                <div>
                  <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Status
                  </p>
                  <Badge className={statusAppearance[selectedQuery.status]}>
                    {selectedQuery.status}
                  </Badge>
                </div>
              </div>
              <div className="space-y-2">
                <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Message
                </p>
                <Input
                  value={selectedQuery.message}
                  readOnly
                  className="rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-800"
                />
              </div>
              {selectedQuery.previousResponse && (
                <div className="space-y-2">
                  <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Previous Response
                  </p>
                  <Input
                    value={selectedQuery.previousResponse}
                    readOnly
                    className="rounded-md border border-slate-200 bg-slate-100 text-sm text-slate-700"
                  />
                </div>
              )}
              <div className="space-y-2">
                <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Response
                </p>
                <Textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  rows={4}
                  placeholder="Type your response..."
                />
              </div>
            </div>
            <DialogFooter className="mt-2 flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleSendReply}>Send Reply</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default ContactUsPage;
